import { useServerFn } from "@tanstack/react-start";
import { urlFor } from "@/lib/sanity";
import { pickLocalized, type Locale } from "@/lib/i18n";
import { AppleSignUpButton, GoogleSignUpButton } from "@/components/ui/app-store-buttons";
import { JoinWaitlistForm } from "@/components/JoinWaitlistForm";
import { submitWaitlist } from "@/lib/waitlist.functions";
import { trackStoreClick } from "@/lib/meta-pixel";
import { TypewriterRotator } from "@/components/TypewriterRotator";

function HeroCTAs({
  ctas,
  locale,
  siteKey,
  onSubmitWaitlist,
}: {
  ctas: any;
  locale: Locale;
  siteKey?: string;
  onSubmitWaitlist: (args: {
    data: { email: string; siteLanguage: Locale; browserLanguage: string; captchaToken: string };
  }) => Promise<{ ok: true } | { ok: false; error?: string }>;
}) {
  const ios = ctas?.ios;
  const android = ctas?.android;
  const waitlist = ctas?.joinWaitlist;
  const showIos = ios?.visible === true;
  const showAndroid = android?.visible === true;
  const showWaitlist = waitlist?.visible === true;
  const iosLabel = showIos ? pickLocalized(ios.wording, locale) || "" : "";
  const androidLabel = showAndroid ? pickLocalized(android.wording, locale) || "" : "";
  const waitlistLabel = showWaitlist ? pickLocalized(waitlist.wording, locale) || "" : "";
  const waitlistPlaceholder = showWaitlist ? pickLocalized(waitlist.placeholder, locale) || "" : "";
  const hasIos = showIos && iosLabel;
  const hasAndroid = showAndroid && androidLabel;
  const hasWaitlist = showWaitlist && waitlistLabel && waitlistPlaceholder;
  if (!hasIos && !hasAndroid && !hasWaitlist) return null;
  return (
    <div className="mt-8 flex flex-col items-stretch gap-4 lg:items-start">
      {(hasIos || hasAndroid) && (
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:items-center lg:justify-start">
          {hasIos && (
            <a
              href={ios.url || "#"}
              onClick={() => trackStoreClick("ios", "hero")}
              target="_blank"
              rel="noopener noreferrer"
              title={
                locale === "fr"
                  ? "Télécharger Matchamind sur l'App Store"
                  : "Download Matchamind on the App Store"
              }
            >
              <AppleSignUpButton variant="black" type="button">
                {iosLabel}
              </AppleSignUpButton>
            </a>
          )}
          {hasAndroid && (
            <a
              href={android.url || "#"}
              onClick={() => trackStoreClick("android", "hero")}
              target="_blank"
              rel="noopener noreferrer"
              title={
                locale === "fr"
                  ? "Obtenir Matchamind sur Google Play"
                  : "Get Matchamind on Google Play"
              }
            >
              <GoogleSignUpButton variant="white" type="button">
                {androidLabel}
              </GoogleSignUpButton>
            </a>
          )}
        </div>
      )}
      {hasWaitlist && (
        <JoinWaitlistForm
          placeholder={waitlistPlaceholder}
          ctaLabel={waitlistLabel}
          successMessage={
            locale === "fr" ? "Merci — vous êtes sur la liste." : "Thanks — you're on the list."
          }
          submittingLabel={locale === "fr" ? "Envoi…" : "Submitting…"}
          genericErrorMessage={
            locale === "fr"
              ? "Une erreur est survenue. Réessayez."
              : "Something went wrong. Please try again."
          }
          siteKey={siteKey}
          onSubmitEmail={async (email, browserLanguage, captchaToken) =>
            onSubmitWaitlist({
              data: { email, siteLanguage: locale, browserLanguage, captchaToken },
            })
          }
          className="mx-auto w-full lg:mx-0"
        />
      )}
    </div>
  );
}

export function HeroSection({
  home,
  locale,
  siteKey,
}: {
  home: any;
  locale: Locale;
  siteKey?: string;
}) {
  const submitWaitlistFn = useServerFn(submitWaitlist);
  const heroLine2 = pickLocalized(home.heroSubtitle, locale) || "Take back control";
  const rotatingPhrases =
    locale === "fr"
      ? ["Discours intérieur dur ?", "Pensées en spirale ?"]
      : ["Harsh self-talk?", "Spiraling thoughts?"];
  const heroBody = pickLocalized(home.heroCaption, locale);

  const localized = home.heroHasLocalizedImage
    ? (home.heroLocalizedImage?.[locale] ?? home.heroLocalizedImage?.en)
    : null;
  const heroImg = localized ?? home.heroImage;
  const heroVersion = home._updatedAt ?? heroImg?.asset?._ref ?? locale;
  const heroUrl = (w: number) => {
    const base = urlFor(heroImg).width(w).auto("format").url();
    return `${base}${base.includes("?") ? "&" : "?"}v=${encodeURIComponent(heroVersion)}`;
  };
  const HERO_WIDTHS = [480, 720, 900];
  const heroSrc = heroImg ? heroUrl(900) : "";
  const heroSrcSet = heroImg ? HERO_WIDTHS.map((w) => `${heroUrl(w)} ${w}w`).join(", ") : "";

  return (
    <section className="px-4 sm:px-6 md:px-[60px]">
      <div className="relative overflow-hidden rounded-[24px] bg-[linear-gradient(180deg,var(--secondary-300)_26%,var(--secondary-600)_99%)] lg:h-[560px] lg:bg-[linear-gradient(89deg,var(--secondary-300)_26%,var(--secondary-600)_99%)]">
        <div className="relative z-10 grid items-center gap-2 lg:h-full lg:grid-cols-[minmax(0,1fr)_368px] lg:gap-8">
          <div className="flex min-w-0 flex-col gap-8 px-6 pt-6 pb-2 text-center lg:justify-center lg:px-[60px] lg:py-[48px] lg:pb-[48px] lg:text-left">
            <div className="mx-auto max-w-xl lg:mx-0">
              {/* The rotating hook is decorative; the stable line below is the h1 so
                  crawlers index a constant, keyword-bearing heading. */}
              <p className="min-h-[1em] font-display text-[32px] font-semibold leading-[1.1] text-text-primary sm:text-[40px] lg:min-h-[1.1em] lg:text-[46px] lg:leading-[52px]">
                <TypewriterRotator phrases={rotatingPhrases} />
              </p>
              <h1 className="mt-3 font-display text-[32px] font-normal leading-[1.1] text-text-primary opacity-90 sm:text-[40px] lg:text-[46px] lg:leading-[52px]">
                {heroLine2}
              </h1>
              {heroBody && (
                <p className="mx-auto mt-8 max-w-md text-[16px] leading-[24px] text-text-secondary lg:mx-0 lg:text-[16px] lg:leading-[24px]">
                  {heroBody}
                </p>
              )}
              <HeroCTAs
                ctas={home.heroCTAs}
                locale={locale}
                siteKey={siteKey}
                onSubmitWaitlist={submitWaitlistFn}
              />
            </div>
          </div>
          {heroImg && (
            <div className="relative flex h-[260px] w-full items-end justify-center px-6 pb-0 sm:h-[320px] lg:h-full lg:items-center lg:justify-center lg:px-0 lg:pr-[60px]">
              <img
                key={heroImg?.asset?._ref ?? locale}
                src={heroSrc}
                srcSet={heroSrcSet}
                sizes="(min-width: 1024px) 308px, 100vw"
                alt={pickLocalized(heroImg?.alt, locale)}
                className="h-full w-full object-cover object-top md:object-contain md:object-center"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
